import { FC, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomPopup from "../../../../custom-components/CustomPopup";
import CustomToast from "../../../../custom-components/CustomToast";
import useToastMessage from "../../../../custom-hooks/useToastMessage";
import { ResponseMessages } from "../../../../domain/enums/common/ResponseMessagesEnum";
import ChainIconSVG from "../../svg/ChainIconSVG";
import ShareButton from "../buttons/ShareButton";

interface SharePopupProps {
    shareUrl?: string;
    shareText?: string;
}

interface ShareActionProps {
    icon: ReactNode;
    label: string;
    href?: string;
    onClick?: () => void;
    first?: boolean;
}

const ShareAction: FC<ShareActionProps> = ({ icon, label, href, onClick, first }) => {
    const className = `flex pl-[64px] dmax2:pl-[49px] items-center w-[410px] dmax2:w-[330px] h-[63px] dmax2:h-[50px] bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] rounded-xl cursor-pointer transition-all duration-300 ${
        first ? "" : "mt-3 dmax2:mt-2"
    }`;
    const content = (
        <>
            {icon}
            <span className="H20 dmax2:H16 text-textWhite">{label}</span>
        </>
    );

    return href ? (
        <a className={className} href={href} target="_blank" rel="noreferrer">
            {content}
        </a>
    ) : (
        <button type="button" className={className} onClick={onClick}>
            {content}
        </button>
    );
};

const SharePopup: FC<SharePopupProps> = ({ shareUrl, shareText = "" }) => {
    const [isVisible, setIsVisible] = useState(false);
    const { t } = useTranslation();
    const { message, setMessage, resetMessage } = useToastMessage();
    const publicUrl = shareUrl || window.location.href;
    const encodedUrl = encodeURIComponent(publicUrl);
    const encodedText = encodeURIComponent(shareText);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(publicUrl);
            setMessage("userProfile.shareMethods.copySuccess", ResponseMessages.SUCCESS);
        } catch {
            setMessage("userProfile.shareMethods.copyFailure", ResponseMessages.DANGER);
        }
    };

    return (
        <CustomPopup
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            wrapClassName="dmin2:min-w-[846px] di6:min-w-[600px] ti6:min-w-[600px] ti7:min-w-[360px]"
            title={t("common.share")}
            titleClassName="mb-8 dmax2:mb-6 mt-2 dmax2:mt-[10px]"
            buttonElement={<ShareButton onClick={() => setIsVisible(true)} />}
        >
            <div className="relative flex flex-col">
                <ShareAction
                    first
                    onClick={copyLink}
                    icon={<ChainIconSVG className="w-9 dmax2:w-6 h-9 dmax2:h-6 mr-4 text-textWhite" />}
                    label={t("userProfile.shareMethods.copyLink")}
                />
                <ShareAction
                    href={`https://t.me/share/url?url=${encodedUrl}${shareText ? `&text=${encodedText}` : ""}`}
                    icon={<img src="/images/telegram.png" alt="" className="w-9 dmax2:w-6 h-9 dmax2:h-6 mr-4" />}
                    label={t("userProfile.shareMethods.telegram")}
                />
                <ShareAction
                    href={`https://wa.me/?text=${encodeURIComponent(shareText ? `${shareText} ${publicUrl}` : publicUrl)}`}
                    icon={<img src="/images/whatsapp.png" alt="" className="w-9 dmax2:w-6 h-9 dmax2:h-6 mr-4" />}
                    label={t("userProfile.shareMethods.whatsapp")}
                />
                <ShareAction
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                    icon={<img src="/images/facebook.png" alt="" className="w-9 dmax2:w-6 h-9 dmax2:h-6 mr-4" />}
                    label={t("userProfile.shareMethods.facebook")}
                />
                <ShareAction
                    onClick={copyLink}
                    icon={<img src="/images/viber.png" alt="" className="w-9 dmax2:w-6 h-9 dmax2:h-6 mr-4" />}
                    label={t("userProfile.shareMethods.viberCopyFallback")}
                />
                <ShareAction
                    onClick={copyLink}
                    icon={<img src="/images/instagram.png" alt="" className="w-9 dmax2:w-6 h-9 dmax2:h-6 mr-4" />}
                    label={t("userProfile.shareMethods.instagramCopyFallback")}
                />
                <CustomToast
                    messageObj={message}
                    resetMessageObj={resetMessage}
                    wrapClassName="max-w-[410px]"
                    singleMessageMode
                />
            </div>
        </CustomPopup>
    );
};

export default SharePopup;
